import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { copyText as copy } from "@/components/ServiceApiGuide";
import { StatusBadge, fmtWhen, fmtDay } from "@/components/HeldUploads";
import { toast } from "sonner";
import { Copy, LineChart, RefreshCw } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/integrations/market-share`;

const curlExample = (key) => `# Push today's workbook (file name carries the date; the title row 'as on YYYY-MM-DD' is authoritative)
curl -sS -H "Authorization: Bearer ${key}" \\
  -F "file=@MARKET_SHARE_$(date +%F).xlsx" ${BASE}/upload
# 200 = loaded · 202 = held for review on the Upload page · 400 = rejected (unreadable). JSON: result, as_on, kerala_totals, rtos, warnings, hold_reasons`;

const pyExample = (key) => `#!/usr/bin/env python3
# Schedule daily (e.g. 07:30 IST). Needs: pip install requests
import datetime, pathlib, sys, requests
KEY = "${key}"
API = "${BASE}/upload"
day = datetime.date.today().isoformat()
path = pathlib.Path(sys.argv[1] if len(sys.argv) > 1 else f"MARKET_SHARE_{day}.xlsx")
with path.open("rb") as f:
    r = requests.post(API, headers={"Authorization": f"Bearer {KEY}"}, files={"file": (path.name, f)}, timeout=120)
res = r.json()
print(r.status_code, res["result"], "as on", res.get("as_on"), res.get("kerala_totals"), res.get("hold_reasons") or res.get("error") or "")
sys.exit(0 if res["result"] == "loaded" else 1)`;

const psExample = (key) => `# PowerShell 7+ (Task Scheduler friendly)
$Key  = "${key}"
$Api  = "${BASE}/upload"
$Day  = Get-Date -Format "yyyy-MM-dd"
$File = if ($args[0]) { $args[0] } else { "MARKET_SHARE_$Day.xlsx" }
$Form = @{ file = Get-Item $File }
try {
  $r = Invoke-RestMethod -Method Post -Uri $Api -Headers @{ Authorization = "Bearer $Key" } -Form $Form
  Write-Output "$($r.result) as on $($r.as_on) Kerala MTD $($r.kerala_totals.MTD) YTD $($r.kerala_totals.YTD) $($r.hold_reasons -join '; ')"
} catch { Write-Output "rejected: $($_.ErrorDetails.Message)"; exit 1 }`;

const TABS = [["curl", "curl", curlExample], ["python", "Python", pyExample], ["ps", "PowerShell", psExample]];

export function MarketFeedPanel({ apiKey }) {
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("curl");
  const key = apiKey || "<API_KEY>";
  const load = useCallback(() => { api.get("/admin/market-feed/status").then(({ data }) => setStatus(data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const code = TABS.find(([k]) => k === tab)[2](key);
  const last = status?.last_run;

  return (
    <div className="rounded-md border p-3 space-y-3" data-testid="ms-feed-panel">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold flex items-center gap-1.5"><LineChart size={14} /> Market-share morning push</h4>
          <p className="text-xs text-muted-foreground">The system that produces <code className="font-mono">MARKET_SHARE_YYYY-MM-DD.xlsx</code> posts it once a day to <code className="font-mono" data-testid="ms-feed-endpoint">POST {BASE.replace(process.env.REACT_APP_BACKEND_URL, "")}/upload</code> with a key from the list above. Clean files load at once; files with unknown RTOs, a Kerala total off by more than 1 %, a TOTAL below the makers' sum or a missing sheet are held on the Upload page for one-click approval.</p>
        </div>
        <Button size="sm" variant="ghost" className="h-7" onClick={load} data-testid="ms-feed-refresh"><RefreshCw size={13} /></Button>
      </div>
      <div className="rounded border bg-muted/40 px-3 py-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="ms-feed-last-run">
        <span className="font-semibold">Last run</span>
        {!last ? <span className="text-muted-foreground">no API push yet</span> : (
          <>
            <StatusBadge status={last.status} testId="ms-feed-last-status" />
            <span>{fmtWhen(last.at)}</span>
            {last.as_on && <span>as on <b className="num">{fmtDay(last.as_on)}</b></span>}
            <span className="text-muted-foreground truncate max-w-[220px]" title={last.file}>{last.file}</span>
            {last.key && <span className="text-muted-foreground">key “{last.key}”</span>}
            {last.warnings > 0 && <span className="text-muted-foreground">{last.warnings} warnings</span>}
            {last.hold_reasons?.length > 0 && <span className="text-amber-800" title={last.hold_reasons.join("; ")}>{last.hold_reasons[0]}</span>}
            {last.error && <span className="text-red-700 truncate max-w-xs" title={last.error}>{last.error}</span>}
          </>
        )}
        {status?.held > 0 && <a href="/uploads" className="ml-auto underline text-amber-800" data-testid="ms-feed-held-link">{status.held} held for review →</a>}
      </div>
      <div>
        <div className="flex items-center gap-1 mb-1">
          {TABS.map(([k, label]) => <button key={k} onClick={() => setTab(k)} className={`px-2 py-1 text-[11px] rounded border transition-colors duration-200 ${tab === k ? "bg-foreground text-background border-foreground" : "hover:bg-secondary"}`} data-testid={`ms-feed-tab-${k}`}>{label}</button>)}
          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => copy(code, `${TABS.find(([k]) => k === tab)[1]} script`)} data-testid="ms-feed-copy"><Copy size={13} className="mr-1" /> Copy</Button>
        </div>
        <pre className="rounded bg-[#0b1220] text-slate-100 p-3 text-[11px] leading-relaxed overflow-auto max-h-64 whitespace-pre" data-testid="ms-feed-script">{code}</pre>
        {!apiKey && <p className="text-[10px] text-muted-foreground mt-1">Create a key above (e.g. “Market share morning job”) — the script fills in automatically while the new key is on screen.</p>}
      </div>
    </div>
  );
}
